import { memo, useMemo, useState } from "react";

function AccessRequestsTab({ c, dark, requests, loading, onRefresh, onApprove, onReject, onResend }) {
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!normalizedQuery) return requests;
    return requests.filter((entry) => {
      const email = String(entry?.email || "").toLowerCase();
      const fullName = String(entry?.full_name || "").toLowerCase();
      return email.includes(normalizedQuery) || fullName.includes(normalizedQuery);
    });
  }, [requests, normalizedQuery]);

  const pending = filtered.filter((entry) => entry.status === "pending");
  const processed = filtered.filter((entry) => entry.status !== "pending");
  const auditItems = [...processed]
    .filter((entry) => !!entry.approved_at)
    .sort((left, right) => new Date(right.approved_at).getTime() - new Date(left.approved_at).getTime())
    .slice(0, 30);

  const sectionTitleStyle = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: c.textSecondary,
    marginBottom: 10,
  };

  const actionButtonStyle = (tone) => ({
    background: tone === "approve"
      ? (dark ? "#0d2d1a" : "#f0fff4")
      : (dark ? "#2d1515" : "#fff5f5"),
    color: tone === "approve"
      ? (dark ? "#48bb78" : "#276749")
      : (dark ? "#fc8181" : "#c53030"),
    border: `1px solid ${tone === "approve"
      ? (dark ? "#48bb78" : "#9ae6b4")
      : (dark ? "#742a2a" : "#feb2b2")}`,
    borderRadius: 8,
    padding: "7px 11px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  });

  const renderRequestCard = (entry, allowActions) => (
    <div
      key={entry.user_id}
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: 12,
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{entry.full_name || "Ohne Namen"}</div>
          <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4 }}>{entry.email}</div>
          <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 6 }}>
            Angefragt: {entry.created_at ? new Date(entry.created_at).toLocaleString() : "-"}
          </div>
          {entry.approved_at && (
            <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 2 }}>
              Bearbeitet: {new Date(entry.approved_at).toLocaleString()}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: entry.status === "approved"
                ? (dark ? "#68d391" : "#276749")
                : entry.status === "rejected"
                ? (dark ? "#fc8181" : "#c53030")
                : (dark ? "#f6ad55" : "#b45309"),
              background: entry.status === "approved"
                ? (dark ? "#0d2d1a" : "#f0fff4")
                : entry.status === "rejected"
                ? (dark ? "#2d1515" : "#fff5f5")
                : (dark ? "#2d2010" : "#fffbeb"),
              border: `1px solid ${entry.status === "approved"
                ? (dark ? "#48bb78" : "#9ae6b4")
                : entry.status === "rejected"
                ? (dark ? "#742a2a" : "#feb2b2")
                : (dark ? "#f6ad55" : "#fbd38d")}`,
              borderRadius: 999,
              padding: "4px 8px",
            }}
          >
            {entry.status}
          </span>
          {allowActions && (
            <>
              <button onClick={() => onApprove(entry)} style={actionButtonStyle("approve")}>Freigeben</button>
              <button onClick={() => onReject(entry)} style={actionButtonStyle("reject")}>Ablehnen</button>
            </>
          )}
          {!allowActions && entry.status === "approved" && (
            <button onClick={() => onResend(entry)} style={actionButtonStyle("approve")}>E-Mail erneut senden</button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "18px 22px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Zugriffsfreigaben</div>
          <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4 }}>
            Neue Benutzer bleiben gesperrt, bis sie hier freigegeben werden.
          </div>
        </div>
        <button
          onClick={onRefresh}
          style={{
            background: c.bgSecondary,
            color: c.text,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: "7px 12px",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {loading ? "Lade..." : "Aktualisieren"}
        </button>
      </div>

      <div style={{ marginBottom: 14 }}>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Nach Name oder E-Mail suchen..."
          style={{
            width: "100%",
            maxWidth: 360,
            background: c.bg,
            color: c.text,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 12,
          }}
        />
      </div>

      <div style={{ marginBottom: 22 }}>
        <div style={sectionTitleStyle}>Ausstehend</div>
        {pending.length === 0
          ? <div style={{ fontSize: 12, color: c.textSecondary }}>Keine offenen Anfragen.</div>
          : pending.map((entry) => renderRequestCard(entry, true))}
      </div>

      <div>
        <div style={sectionTitleStyle}>Bearbeitet</div>
        {processed.length === 0
          ? <div style={{ fontSize: 12, color: c.textSecondary }}>Noch keine bearbeiteten Freigaben.</div>
          : processed.map((entry) => renderRequestCard(entry, false))}
      </div>

      <div style={{ marginTop: 24 }}>
        <div style={sectionTitleStyle}>Audit Log (letzte Aktionen)</div>
        {auditItems.length === 0 ? (
          <div style={{ fontSize: 12, color: c.textSecondary }}>Noch keine protokollierten Aktionen.</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {auditItems.map((entry) => (
              <div key={`audit-${entry.user_id}-${entry.approved_at}`} style={{ fontSize: 12, color: c.textSecondary, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: "8px 10px" }}>
                {new Date(entry.approved_at).toLocaleString()} - {entry.status.toUpperCase()} - {entry.email}
                {entry.approved_by ? ` (Admin-ID: ${entry.approved_by})` : ""}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(AccessRequestsTab);